import { useCallback, useEffect, useState } from 'react';
import { marketingApi, type MarketingJourney } from '@/api/marketing.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { JOURNEY_TRIGGERS } from './constants';
import { MarketingStatusBadge } from './components/MarketingStatusBadge';
import { MarketingEmptyState } from './components/MarketingEmptyState';
import { JourneyCanvasEditor } from './components/JourneyCanvasEditor';
import { notify } from '@/core/toast/notify';

const DEFAULT_CANVAS = (trigger: string) =>
  JSON.stringify(
    {
      nodes: [
        { key: 'start', type: 'TRIGGER', label: trigger, x: 40, y: 80 },
        { key: 'email', type: 'ACTION', label: 'Send email', x: 220, y: 80 },
        { key: 'wait', type: 'DELAY', label: 'Wait 2 days', x: 400, y: 80 },
        { key: 'goal', type: 'GOAL', label: 'Converted', x: 580, y: 80 },
      ],
      edges: [
        { from: 'start', to: 'email' },
        { from: 'email', to: 'wait' },
        { from: 'wait', to: 'goal' },
      ],
    },
    null,
    2,
  );

export function MarketingJourneysPage() {
  const [journeys, setJourneys] = useState<MarketingJourney[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('LEAD_CAPTURED');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<MarketingJourney | null>(null);
  const [canvasText, setCanvasText] = useState('');
  const [canvasSaving, setCanvasSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setJourneys(await marketingApi.listJourneys());
    } catch (err) {
      showApiError(err, 'Failed to load journeys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectForEdit = (j: MarketingJourney) => {
    setEditing(j);
    setCanvasText(j.canvasJson?.trim() ? j.canvasJson : DEFAULT_CANVAS(j.triggerEvent));
  };

  const create = async () => {
    setSaving(true);
    try {
      const created = await marketingApi.createJourney({
        journeyName: name.trim() || 'New journey',
        triggerEvent: trigger,
        canvasJson: DEFAULT_CANVAS(trigger),
      });
      notify.success('Journey created');
      setName('');
      await load();
      selectForEdit(created);
    } catch (err) {
      showApiError(err, 'Failed to create journey');
    } finally {
      setSaving(false);
    }
  };

  const saveCanvas = async () => {
    if (!editing) return;
    setCanvasSaving(true);
    try {
      const updated = await marketingApi.updateJourney(editing.id, { canvasJson: canvasText });
      notify.success('Canvas saved');
      setEditing(updated);
      load();
    } catch (err) {
      showApiError(err, 'Failed to save canvas');
    } finally {
      setCanvasSaving(false);
    }
  };

  const journeyAction = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      notify.success(msg);
      load();
    } catch (err) {
      showApiError(err, 'Action failed');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Journeys"
        description="Automate nurture flows with triggers, delays, and goals — edit the journey graph as JSON with live preview."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Marketing', to: '/marketing' },
          { label: 'Journeys' },
        ]}
      />

      <section className="card flex flex-wrap gap-3 items-end p-4">
        <div className="flex-1 min-w-[160px]">
          <Input
            label="Name"
            placeholder="Welcome series"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="min-w-[180px]">
          <Select
            label="Trigger"
            options={JOURNEY_TRIGGERS.map((t) => ({ value: t.value, label: t.label }))}
            value={trigger}
            onChange={setTrigger}
          />
        </div>
        <Button loading={saving} onClick={() => void create()}>
          Create journey
        </Button>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="card">
          <h2 className="p-4 text-sm font-semibold border-b border-slate-100">Your journeys</h2>
          {loading ? (
            <p className="p-4 text-sm text-slate-500">Loading…</p>
          ) : journeys.length === 0 ? (
            <MarketingEmptyState title="No journeys" description="Create a journey to automate follow-ups after key events." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {journeys.map((j) => (
                <li key={j.id} className="p-4 flex flex-wrap justify-between items-center gap-3">
                  <button
                    type="button"
                    className="text-left"
                    onClick={() => selectForEdit(j)}
                  >
                    <p className="font-medium text-slate-800 text-sm">{j.journeyName}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{j.triggerEvent}</p>
                  </button>
                  <div className="flex items-center gap-2">
                    <MarketingStatusBadge status={j.journeyStatus} kind="journey" />
                    <Button variant="ghost" className="text-xs" onClick={() => selectForEdit(j)}>
                      Edit canvas
                    </Button>
                    {j.journeyStatus === 'DRAFT' && (
                      <Button
                        variant="ghost"
                        className="text-xs"
                        onClick={() => journeyAction(() => marketingApi.submitJourneyApproval(j.id), 'Submitted')}
                      >
                        Submit
                      </Button>
                    )}
                    {j.journeyStatus !== 'ACTIVE' && (
                      <Button
                        variant="ghost"
                        className="text-xs text-emerald-700"
                        onClick={() => journeyAction(() => marketingApi.activateJourney(j.id), 'Activated')}
                      >
                        Activate
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card p-4">
          {editing ? (
            <>
              <h2 className="text-sm font-semibold text-slate-800 mb-1">{editing.journeyName}</h2>
              <p className="text-xs text-slate-500 mb-4">Graph stored in journey nodes/edges on save.</p>
              <JourneyCanvasEditor
                canvasText={canvasText}
                onCanvasTextChange={setCanvasText}
                onSave={() => void saveCanvas()}
                saving={canvasSaving}
                readOnly={editing.journeyStatus === 'ACTIVE'}
              />
            </>
          ) : (
            <p className="text-sm text-slate-500">Select a journey to edit its canvas JSON.</p>
          )}
        </section>
      </div>
    </div>
  );
}
